import { AudioContext } from './AudioProvider';
import { AudioContextShape } from './models/AudioContextShape';
import Slider from './Slider';
import stepBackImage from './images/stepBack.svg';
import stepForwardImage from './images/stepForward.svg';
import style from './styles/App.module.scss';
import { FormattedMessage, useIntl } from 'react-intl';
import type { MouseEvent, ReactElement } from 'react';
import { useContext, useEffect, useState } from 'react';

const SEC_PER_MIN = 60;
const STEP_AMOUNT = 15;

export default function Scrubber(): ReactElement {
    const intl = useIntl();
    const audio = useContext<AudioContextShape>(AudioContext);

    const [markerLeft, setMarkerLeft] = useState<number>(0);
    const [time, setTime] = useState<{ tElapsed: string, tRemaining: string, tVal: number}>({ tElapsed: '', tRemaining: '', tVal: 0 });
    const [isMoving, setIsMoving] = useState<boolean>(false);
    const [durationMinutes, setDurationMinutes] = useState<string>(null);
    
    useEffect(() => {
        if (audio?.state?.duration) {
            setDurationMinutes(intl.formatNumber(audio.state.duration / SEC_PER_MIN, { maximumFractionDigits: 0 }));
        }
    }, [audio?.state?.duration]);
    
    
    useEffect(() => {
        if (audio) {
            if (!isMoving) {
                setMarkerLeft(audio.state.currentTime / audio.state.duration);
                setTime(makeTime(audio.state.currentTime));
            }
        }
    }, [audio?.state?.currentTime, audio?.state?.duration]);

    const makeTime = (number) => {
        const remaining = audio.state.duration - number;
        let remMinutes, remSeconds;
        if (Math.ceil(remaining % SEC_PER_MIN) === SEC_PER_MIN) {
            remMinutes = Math.ceil(remaining / SEC_PER_MIN);
            remSeconds = 0;
        } else if (remaining <= 0) {
            remMinutes = 0;
            remSeconds = 0;
        } else {
            remMinutes = Math.floor(remaining / SEC_PER_MIN);
            remSeconds = Math.ceil(remaining % SEC_PER_MIN);
        }
        if (isNaN(remMinutes) || isNaN(remSeconds)) {
            remMinutes = 0;
            remSeconds = 0;
        }
        return {
            tElapsed: intl.formatMessage(
                { id: 'web_audio_player_time_elapsed', defaultMessage: '{0}:{1}' },
                { '0': intl.formatNumber(Math.floor(number / SEC_PER_MIN), { minimumIntegerDigits: 1 }), '1': intl.formatNumber(Math.floor(number % SEC_PER_MIN), { minimumIntegerDigits: 2 }) }
            ),
            tRemaining: intl.formatMessage(
                { id: 'web_audio_player_time_remaining', defaultMessage: '-{0}:{1}' },
                { '0': intl.formatNumber(remMinutes, { minimumIntegerDigits: 1 }), '1': intl.formatNumber(remSeconds, { minimumIntegerDigits: 2 }) }
            ),
            tVal: number,
        };
    };

    const handleMove = (e: MouseEvent, percent, force) => {
        if (isMoving || force) {
            if (e) {
                e.preventDefault();
                e.stopPropagation();
            }
            const posTime = audio.state.duration * percent;
            setMarkerLeft(percent);
            setTime(makeTime(posTime));
        }
    };

    const handleJump = (e, percent) => {
        setIsMoving(true);
        handleMove(e, percent, true);
    };

    const handleDrag = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        setIsMoving(true);
    };

    const handleDrop = (e) => {
        if (isMoving) {
            const wePaused = !audio.state.paused;
            audio.state.pause();
            if (e) {
                e.preventDefault();
                e.stopPropagation();
            }
            audio.state.setCurrentTime(time.tVal);
            setIsMoving(false);
            if (wePaused) audio.state.play();
        }
    };
    
    const stepBack = () => {
        audio.state.setCurrentTime(audio.state.currentTime - STEP_AMOUNT);
    };
    
    const stepForward = () => {
        audio.state.setCurrentTime(audio.state.currentTime + STEP_AMOUNT);
    };

    return (<>
        <Slider
            markerLeft={markerLeft * 100 + '%'}
            onClick={handleJump}
            onMarkerDown={handleDrag}
            onUp={handleDrop}
            onMove={handleMove}
        >
            <div className={style.scrubberText}>
                <div className={style.scrubberTime}>
                    <button className={style.step} onClick={stepBack}>
                        <img src={stepBackImage} alt={'Step back 15 seconds'} />
                    </button>
                    <div>{time.tElapsed}</div>
                </div>
                <div className={style.scrubberTime}>
                    <div>{time.tRemaining}</div>
                    <button className={style.step} onClick={stepForward}>
                        <img src={stepForwardImage} alt={'Step forward 15 seconds'} />
                    </button>
                </div>
            </div>
        </Slider>
        <div className={style.specialTime}><FormattedMessage id={'add_session_minutes_format'} defaultMessage={'{0} min'} values={{ '0': durationMinutes }} /></div>
    </>
    );
}